import { Component, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormArray,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { SwitchWebhookService } from 'src/app/_services/switch-webhook.service';
import { UserService } from 'src/app/_services';
import { DialogService } from 'primeng/dynamicdialog';
import { SwitchWebhookDialogComponent } from '../switch-webhook-dialog/switch-webhook-dialog.component';
import { ConfirmationService } from 'primeng/api';
import { MessageService } from 'primeng/api';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-switch-webhook-admin',
  templateUrl: './switch-webhook-admin.component.html',
  styleUrls: ['./switch-webhook-admin.component.scss'],
})
export class SwitchWebhookAdminComponent implements OnInit {
  webhooks: any[] = [];
  user: any;
  loading: boolean = false;
  userWebhooks: any[] = [];
  userRole: any;
  user_id: any;

  users: any[] = [];

  // Assign Webhooks Feature
  showAssignSection: boolean = false;
  availableUsers: any[] = [];
  selectedUser: any = null;
  availableWebhooks: any[] = [];
  webhookSearchTerm: string = '';

  constructor(
    private fb: FormBuilder,
    private switchWebhookService: SwitchWebhookService,
    private userService: UserService,
    private dialogService: DialogService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.loading = true;

    this.userService.currentUser$.subscribe((user) => {
      this.userRole = user?.role?.name;
      this.user_id = user.id;

      this.switchWebhookService.getAdminSwitchCustomers(this.user_id).subscribe(
        (data: any) => {
          for (let users of data.body) {
            if (!this.users.find((u) => u.id === users.user_id)) {
              this.userService
                .getUserById(users.user_id)
                .subscribe((res: any) => {
                  this.users.push(res);
                  console.log('Switch Users', this.users);
                });
            }
          }
        },
        (error) => {}
      );
    });

    this.switchWebhookService.getWebhookByAdminId().subscribe(
      (response: any) => {
        const groupedWebhooks: { [key: number]: any } = {};

        // Group webhooks by user_id
        response.body.forEach((webhook: any) => {
          if (!groupedWebhooks[webhook.user_id]) {
            groupedWebhooks[webhook.user_id] = {
              user_id: webhook.user_id, // Placeholder until user details are fetched
              user_details: null, // Will be replaced with user details
              webhooks: [],
            };
          }
          groupedWebhooks[webhook.user_id].webhooks.push(webhook);
        });

        // Get unique user IDs
        const userIds = Object.keys(groupedWebhooks).map((id) => Number(id));

        // If no webhooks, set loading to false immediately
        if (userIds.length === 0) {
          this.userWebhooks = [];
          this.loading = false;
          return;
        }

        // Fetch user details for each user_id
        const userRequests = userIds.map((id) =>
          this.userService.getUserById(id)
        );

        forkJoin(userRequests).subscribe({
          next: (users) => {
            users.forEach((user) => {
              if (user.id !== undefined && groupedWebhooks[user.id]) {
                groupedWebhooks[user.id].user_details = user;
              }
            });

            // Convert object to array
            this.userWebhooks = Object.values(groupedWebhooks);
            this.loading = false;

            this.availableWebhooks = this.userWebhooks.flatMap(
              (uw) => uw.webhooks
            );

            console.log('User Webhooks:', this.userWebhooks);
          },
          error: (error) => {
            console.error('Error fetching user details:', error);
            this.loading = false;
          },
        });
      },
      (error) => {
        console.error('Error fetching webhooks:', error);
        this.loading = false;
      }
    );
  }

  openDialog(webhook: any) {
    const ref = this.dialogService.open(SwitchWebhookDialogComponent, {
      header: 'Submit Webhook',
      width: '70%',
      data: webhook,
    });
  }

  get filteredAvailableWebhooks() {
    console.log('availableWebhooks', this.availableWebhooks);

    if (!this.webhookSearchTerm) {
      return this.availableWebhooks;
    }
    const search = this.webhookSearchTerm.toLowerCase();
    return this.availableWebhooks.filter(
      (webhook) =>
        webhook.webhook_name?.toLowerCase().includes(search) ||
        webhook.webhook_url?.toLowerCase().includes(search) ||
        webhook.description?.toLowerCase().includes(search)
    );
  }

  hasSelectedWebhooks(): boolean {
    return this.availableWebhooks.some((webhook) => webhook.selected);
  }

  assignWebhooks() {
    const selectedWebhooks = this.availableWebhooks.filter(
      (webhook) => webhook.selected
    );
    if (!this.selectedUser || selectedWebhooks.length === 0) {
      return;
    }

    this.switchWebhookService
      .assignWebhooksToUser(
        selectedWebhooks.map((w) => w.id),
        this.selectedUser.id
      )
      .subscribe(
        () => {
          // Show success message
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: `Assigned ${selectedWebhooks.length} webhook(s) to ${this.selectedUser.username}`,
            key: 'switch-webhook-success',
          });

          // Reset form
          this.showAssignSection = false;
          this.selectedUser = null;
          this.availableWebhooks.forEach(
            (webhook) => (webhook.selected = false)
          );
          this.webhookSearchTerm = '';
        },
        (error) => {
          // Handle error
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to assign webhooks',
            key: 'switch-webhook-error',
          });
        }
      );
  }

  onUserChange() {
    this.switchWebhookService
      .getUnassignedWebhooks(this.selectedUser.id)
      .subscribe(
        (response: any) => {
          console.log('Unassigned Webhooks:', response.body);

          this.availableWebhooks = response.body;
        },
        (error) => {
          console.error('Error fetching unassigned webhooks:', error);
        }
      );
  }

  deleteWebhook(webhook: any) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete ${webhook.webhook_name}?`,
      accept: () => {
        this.switchWebhookService.deleteSwitchWebhookData(webhook.id).subscribe(
          () => {
            // Find the userWebhook containing the webhook
            this.userWebhooks.forEach((userWebhook) => {
              userWebhook.webhooks = userWebhook.webhooks.filter(
                (wh: any) => wh.id !== webhook.id
              );
            });

            // Optionally, remove empty userWebhooks if needed
            this.userWebhooks = this.userWebhooks.filter(
              (userWebhook) => userWebhook.webhooks.length > 0
            );

            // Show success message
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Webhook Deleted Successfully',
              key: 'delete_webhook',
            });
          },
          (error) => {
            // Handle error
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to delete the webhook',
              key: 'delete_webhook',
            });
          }
        );
      },
    });
  }
}
